import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatTime, Icon, usePolling } from '@pryme/ui';
import { ActivationPanel } from '../components/ActivationPanel';
import { CopyField } from '../components/CodeBlock';
import { EventList } from '../components/EventList';
import { useServiceOutlet } from '../components/ServiceLayout';
import { activeKeys, type DevEvent } from '../lib/types';
import { LocalTag } from '../components/OneTimeSecretPanel';

export function ServiceOverview() {
  const { service: s, reload } = useServiceOutlet();
  const [events, setEvents] = useState<DevEvent[]>([]);
  const load = useCallback(async () => {
    const r = await api<{ events: DevEvent[] }>(`/v1/services/${s.id}/events`).catch(() => ({ events: [] }));
    setEvents(r.events.slice(0, 10));
  }, [s.id]);
  useEffect(() => {
    void load();
  }, [load]);
  usePolling(() => void load(), 10000);

  const checklist = [
    { label: 'Service created', ok: true, to: '' },
    { label: 'Redirect URIs registered', ok: s.redirectUris.length > 0, to: 'redirects' },
    ...(s.environment === 'production' ? [{ label: 'Redirect URIs on a verified organization domain', ok: !!s.domain, to: '/domains' }] : []),
    { label: 'Credentials issued', ok: s.status !== 'pending_credentials', to: 'credentials' },
    { label: 'Activated with a signed challenge', ok: s.status === 'active', to: '' },
  ];

  return (
    <>
      <div className="p-grid">
        <section className="p-card" aria-labelledby="setup-h">
          <h2 id="setup-h">Setup</h2>
          <ul className="dv-checks">
            {checklist.map((c) => (
              <li key={c.label} className={c.ok ? 'ok' : 'todo'}>
                <Icon name={c.ok ? 'check' : 'clock'} size={16} label={c.ok ? 'Done' : 'To do'} />
                {c.to && !c.ok ? <Link to={c.to}>{c.label}</Link> : c.label}
              </li>
            ))}
          </ul>
        </section>
        <section className="p-card" aria-labelledby="facts-h">
          <h2 id="facts-h">At a glance</h2>
          <dl className="p-kv">
            {s.environment === 'production' && <><dt>Verified domain</dt><dd>{s.domain ? <code>{s.domain}</code> : <Link to="/domains">none yet</Link>}</dd></>}
            <dt>Connected people</dt><dd>{s.connections} <span className="p-muted p-small">· pairwise; you never see a global ID</span></dd>
            <dt>Active keys</dt><dd>{activeKeys(s, 'B').length} signing · {activeKeys(s, 'A').length} Pryme verification · <Link to="credentials">manage</Link></dd>
            <dt>Requested claims</dt><dd>{s.claimConfig.requested.map((c) => <code key={c} className="p-mono-chip" style={{ marginRight: 4 }}>{c}</code>)}</dd>
            <dt>Created</dt><dd>{formatTime(s.createdAt)}</dd>
          </dl>
        </section>
      </div>

      <section className="p-card" aria-labelledby="ids-h">
        <h2 id="ids-h">Integration identifiers</h2>
        <div className="dv-cols">
          <CopyField label="Service ID (client_id)" envVar="PRYME_SERVICE_ID" value={s.id} />
          <CopyField tag={<LocalTag url={s.apiBase} />} label="API base" value={s.apiBase} />
          <CopyField label="API audience" value={s.apiAudience} hint="Part of every signed request. Your secret key carries it; the SDK uses it for you." />
          <CopyField label="Issuer" value={s.issuer} />
        </div>
      </section>

      {s.status !== 'active' && (
        <section className="p-card" aria-labelledby="act-h">
          <h2 id="act-h">Activation</h2>
          <ActivationPanel service={s} onActivated={() => void reload()} />
        </section>
      )}

      <section className="p-card" aria-labelledby="ev-h">
        <div className="p-row p-between">
          <h2 id="ev-h">Recent events</h2>
          <Link to="events" className="p-small">All events <Icon name="arrowRight" size={13} /></Link>
        </div>
        <EventList events={events} compact />
      </section>
    </>
  );
}
